import { useState, useEffect, useMemo } from "react";
import {
  ThemeProvider,
  useMediaQuery,
  CssBaseline,
  GlobalStyles,
} from "@mui/material";
import { ThemeContext, type ThemeMode } from "./ThemeContext";
import { createAppTheme } from "./createAppTheme";

export function ThemeContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  const [mode, setColorMode] = useState<ThemeMode>(() => {
    return (localStorage.getItem("wingo_ui_theme") as ThemeMode) || "dark";
  });
  const setMode = (newMode: ThemeMode, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!document.startViewTransition) {
      setColorMode(newMode);
      return;
    }
    const x = event.clientX;
    const y = event.clientY;
    const transition = document.startViewTransition(() => {
      setColorMode(newMode);
    });
    transition.ready.then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${Math.hypot(window.innerWidth, window.innerHeight)}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration: 500,
          easing: "ease-in-out",
          pseudoElement: "::view-transition-new(root)",
        },
      );
    });
  };
  useEffect(() => {
    localStorage.setItem("wingo_ui_theme", mode);
  }, [mode]);

  const actualMode =
    mode === "system" ? (prefersDarkMode ? "dark" : "light") : mode;

  const theme = useMemo(() => createAppTheme(actualMode), [actualMode]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, actualMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            "html, body, #root": {
              width: "100%",
              height: "100%",
              margin: 0,
              padding: 0,
              transition: "background-color 0.3s ease, color 0.3s ease",
            },
            "*::-webkit-scrollbar": { width: "6px" },
            "*::-webkit-scrollbar-track": { background: "transparent" },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: actualMode === "dark" ? "#424242" : "#cbd5e1",
              borderRadius: "10px",
            },
            "*::-webkit-scrollbar-thumb:hover": {
              backgroundColor: actualMode === "dark" ? "#616161" : "#94a3b8",
            },
            "*": {
              scrollbarWidth: "thin",
              scrollbarColor: `${actualMode === "dark" ? "#424242" : "#cbd5e1"} transparent`,
            },
          }}
        />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}